import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:   <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:  <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    MapPin:      <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Search:      <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
    ChevronRight:<><path d="m9 18 6-6-6-6"/></>,
    ChevronDown: <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:   <><path d="m18 15-6-6-6 6"/></>,
    Users:       <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    Zap:         <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Shield:      <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    Star:        <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    Sparkles:    <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3z"/></>,
    CheckCircle: <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    Navigation:  <><polygon points="3 11 22 2 13 21 11 13 3 11"/></>,
    Clock:       <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Wrench:      <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></>,
    Globe:       <><circle cx="12" cy="12" r="10"/><line x1="2" x2="22" y1="12" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Data ── */
const CITIES = [
  {
    id: "nairobi",
    name: "Nairobi",
    tag: "Most active",
    tagColor: T.g500,
    tagBg: T.g50,
    tagBorder: T.g100,
    desc: "Our largest and most active market. Hundreds of verified JiraniPros across every major estate and neighbourhood, covering the full range of home services.",
    pros: "500+",
    areas: [
      { name: "Westlands",        popular: ["Cleaning", "Electrical", "Plumbing"] },
      { name: "Karen",            popular: ["Gardening", "Cleaning", "Painting"] },
      { name: "Kilimani",         popular: ["Plumbing", "Electrical", "Cleaning"] },
      { name: "Lavington",        popular: ["Cleaning", "Carpentry", "Painting"] },
      { name: "South B",          popular: ["Electrical", "Plumbing", "Cleaning"] },
      { name: "South C",          popular: ["Plumbing", "Cleaning", "Electrical"] },
      { name: "Langata",          popular: ["Gardening", "Cleaning", "Painting"] },
      { name: "Hurlingham",       popular: ["Electrical", "Plumbing", "Cleaning"] },
      { name: "Kileleshwa",       popular: ["Cleaning", "Plumbing", "Carpentry"] },
      { name: "Parklands",        popular: ["Electrical", "Cleaning", "Plumbing"] },
      { name: "Muthaiga",         popular: ["Gardening", "Cleaning", "Security"] },
      { name: "Gigiri",           popular: ["Cleaning", "Gardening", "Electrical"] },
      { name: "Ruaka",            popular: ["Plumbing", "Electrical", "Painting"] },
      { name: "Kasarani",         popular: ["Electrical", "Plumbing", "Cleaning"] },
      { name: "Embakasi",         popular: ["Plumbing", "Electrical", "Cleaning"] },
      { name: "Donholm",          popular: ["Plumbing", "Cleaning", "Painting"] },
      { name: "Buruburu",         popular: ["Electrical", "Cleaning", "Plumbing"] },
      { name: "Eastleigh",        popular: ["Plumbing", "Electrical", "Cleaning"] },
      { name: "Roysambu",         popular: ["Cleaning", "Plumbing", "Electrical"] },
      { name: "Ruiru",            popular: ["Plumbing", "Painting", "Electrical"] },
      { name: "Thika Road",       popular: ["Electrical", "Plumbing", "Cleaning"] },
      { name: "Rongai",           popular: ["Cleaning", "Plumbing", "Gardening"] },
      { name: "Ngong Road",       popular: ["Electrical", "Cleaning", "Plumbing"] },
      { name: "Upper Hill",       popular: ["Cleaning", "Electrical", "Plumbing"] },
    ],
  },
  {
    id: "mombasa",
    name: "Mombasa",
    tag: "Growing fast",
    tagColor: "#92400e",
    tagBg: "#fffbeb",
    tagBorder: "#fde68a",
    desc: "A rapidly growing JiraniApp market with strong demand for home services across the island and mainland. Professionals available in the core areas and expanding.",
    pros: "120+",
    areas: [
      { name: "Nyali",         popular: ["Cleaning", "Plumbing", "Electrical"] },
      { name: "Bamburi",       popular: ["Electrical", "Cleaning", "Painting"] },
      { name: "Tudor",         popular: ["Plumbing", "Electrical", "Cleaning"] },
      { name: "Likoni",        popular: ["Cleaning", "Plumbing", "Painting"] },
      { name: "Shanzu",        popular: ["Gardening", "Cleaning", "Electrical"] },
      { name: "Kisauni",       popular: ["Plumbing", "Cleaning", "Electrical"] },
      { name: "Mombasa CBD",   popular: ["Electrical", "Plumbing", "Cleaning"] },
      { name: "Changamwe",     popular: ["Plumbing", "Electrical", "Cleaning"] },
    ],
  },
  {
    id: "kisumu",
    name: "Kisumu",
    tag: "Available",
    tagColor: T.n600,
    tagBg: T.n50,
    tagBorder: T.n150,
    desc: "JiraniApp is live in Kisumu with a growing network of verified professionals. Coverage expanding across key residential and commercial areas.",
    pros: "60+",
    areas: [
      { name: "Milimani",      popular: ["Cleaning", "Electrical", "Plumbing"] },
      { name: "Migosi",        popular: ["Plumbing", "Cleaning", "Painting"] },
      { name: "Nyalenda",      popular: ["Electrical", "Plumbing", "Cleaning"] },
      { name: "Kondele",       popular: ["Cleaning", "Plumbing", "Electrical"] },
      { name: "Mamboleo",      popular: ["Electrical", "Cleaning", "Painting"] },
      { name: "Kisumu CBD",    popular: ["Plumbing", "Electrical", "Cleaning"] },
    ],
  },
  {
    id: "nakuru",
    name: "Nakuru",
    tag: "Available",
    tagColor: T.n600,
    tagBg: T.n50,
    tagBorder: T.n150,
    desc: "Home service professionals are active across Nakuru's main residential zones. We are growing our network across the county steadily.",
    pros: "45+",
    areas: [
      { name: "Milimani",       popular: ["Cleaning", "Electrical", "Plumbing"] },
      { name: "Section 58",     popular: ["Plumbing", "Cleaning", "Painting"] },
      { name: "Lanet",          popular: ["Electrical", "Plumbing", "Cleaning"] },
      { name: "Nakuru CBD",     popular: ["Cleaning", "Plumbing", "Electrical"] },
      { name: "London",         popular: ["Electrical", "Cleaning", "Painting"] },
      { name: "Rhonda",         popular: ["Plumbing", "Electrical", "Cleaning"] },
    ],
  },
];

/* ── Area pill ── */
function AreaPill({ name, popular, cityName, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={`Browse pros in ${name} — top services: ${popular.join(", ")}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        padding: "9px 14px", borderRadius: 10,
        border: `1.5px solid ${hovered ? T.g500 : T.n150}`,
        background: hovered ? T.g50 : T.white,
        cursor: "pointer", fontFamily: T.sans,
        fontSize: 13, fontWeight: 500,
        color: hovered ? T.g700 : T.n700,
        transition: "all .15s", textAlign: "left",
      }}>
      <Icon name="MapPin" size={12} color={hovered ? T.g500 : T.n400} strokeWidth={2} />
      {name}
      <Icon name="ChevronRight" size={12} color={hovered ? T.g400 : T.n300} strokeWidth={2} />
    </button>
  );
}

/* ── City card ── */
function CityCard({ city, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const navigate = useNavigate();

  const handleAreaClick = (areaName) => {
    navigate(`/search?location=${encodeURIComponent(areaName + ", " + city.name)}`);
  };

  const handleCitySearch = () => {
    navigate(`/search?location=${encodeURIComponent(city.name)}`);
  };

  return (
    <div style={{
      borderRadius: 18,
      border: `1.5px solid ${open ? T.g200 : T.n150}`,
      background: T.white,
      overflow: "hidden",
      transition: "border-color .2s",
      marginBottom: 16,
    }}>
      {/* Card header */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center",
          justifyContent: "space-between", padding: "22px 24px",
          background: "none", border: "none", cursor: "pointer",
          fontFamily: T.sans, textAlign: "left", gap: 16,
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 0 }}>
          {/* City icon */}
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: open ? T.g50 : T.n50,
            border: `1.5px solid ${open ? T.g100 : T.n150}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all .2s",
          }}>
            <Icon name="Globe" size={19} color={open ? T.g500 : T.n400} strokeWidth={1.6} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 3 }}>
              <span style={{ fontSize: 17, fontWeight: 600, color: T.n900 }}>{city.name}</span>
              <span style={{
                fontSize: 11, fontWeight: 600, letterSpacing: "0.05em",
                color: city.tagColor, background: city.tagBg,
                border: `1px solid ${city.tagBorder}`,
                borderRadius: 99, padding: "2px 9px",
              }}>{city.tag}</span>
            </div>
            <div style={{ fontSize: 13, color: T.n400 }}>
              {city.pros} professionals available
            </div>
          </div>
        </div>

        <div style={{ flexShrink: 0, color: T.n400 }}>
          <Icon name={open ? "ChevronUp" : "ChevronDown"} size={18} strokeWidth={2} />
        </div>
      </button>

      {/* Expanded content */}
      {open && (
        <div style={{ padding: "0 24px 24px", borderTop: `1px solid ${T.n100}` }}>
          <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.7, margin: "18px 0 20px" }}>
            {city.desc}
          </p>

          {/* Areas */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.n400, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
              Browse by area
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {city.areas.map(area => (
                <AreaPill
                  key={area.name}
                  name={area.name}
                  popular={area.popular}
                  cityName={city.name}
                  onClick={() => handleAreaClick(area.name)}
                />
              ))}
            </div>
          </div>

          {/* Popular services in this city */}
          <div style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.n400, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
              Most booked in {city.name}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {["Cleaning", "Plumbing", "Electrical", "Painting", "Gardening"].map(svc => (
                <button
                  key={svc}
                  onClick={() => navigate(`/search?location=${encodeURIComponent(city.name)}&q=${encodeURIComponent(svc)}`)}
                  style={{
                    padding: "6px 14px", borderRadius: 99,
                    border: `1px solid ${T.g100}`, background: T.g50,
                    fontSize: 12, fontWeight: 500, color: T.g700,
                    cursor: "pointer", fontFamily: T.sans,
                    display: "inline-flex", alignItems: "center", gap: 5,
                    transition: "all .15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = T.g100; e.currentTarget.style.borderColor = T.g200; }}
                  onMouseLeave={e => { e.currentTarget.style.background = T.g50; e.currentTarget.style.borderColor = T.g100; }}>
                  <Icon name="Wrench" size={11} color={T.g500} strokeWidth={2} />
                  {svc}
                </button>
              ))}
            </div>
          </div>

          {/* City-level CTA */}
          <button
            onClick={handleCitySearch}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "11px 22px", background: T.g500, color: T.white,
              border: "none", borderRadius: 10, fontSize: 13, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
            }}
            onMouseEnter={e => (e.currentTarget.style.background = T.g600)}
            onMouseLeave={e => (e.currentTarget.style.background = T.g500)}>
            <Icon name="Search" size={14} color={T.white} strokeWidth={2} />
            Browse all pros in {city.name}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Info row ── */
function InfoRow({ icon, title, desc }) {
  return (
    <div style={{ display: "flex", gap: 16, padding: "18px 0", borderBottom: `1px solid ${T.n100}` }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name={icon} size={16} color={T.g500} strokeWidth={1.7} />
      </div>
      <div style={{ flex: 1, paddingTop: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.n800, marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 14, color: T.n600, lineHeight: 1.65 }}>{desc}</div>
      </div>
    </div>
  );
}

/* ── Live search bar ── */
function LocationSearchBar({ onSearch }) {
  const [query, setQuery] = useState("");
  return (
    <div style={{ position: "relative", maxWidth: 480 }}>
      <div style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
        <Icon name="Search" size={16} color={T.n400} strokeWidth={2} />
      </div>
      <input
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => e.key === "Enter" && query.trim() && onSearch(query.trim())}
        placeholder="Search a neighbourhood, estate, or city..."
        style={{
          width: "100%", padding: "13px 48px 13px 42px",
          border: `1.5px solid ${T.n150}`, borderRadius: 12,
          fontSize: 14, fontFamily: T.sans, color: T.n900,
          background: T.white, outline: "none", boxSizing: "border-box",
          transition: "border-color .15s",
        }}
        onFocus={e => (e.target.style.borderColor = T.g500)}
        onBlur={e => (e.target.style.borderColor = T.n150)}
      />
      {query && (
        <button
          onClick={() => onSearch(query.trim())}
          style={{
            position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
            background: T.g500, border: "none", borderRadius: 8,
            padding: "6px 12px", cursor: "pointer",
            fontSize: 12, fontWeight: 600, color: T.white, fontFamily: T.sans,
          }}
          onMouseEnter={e => (e.currentTarget.style.background = T.g600)}
          onMouseLeave={e => (e.currentTarget.style.background = T.g500)}>
          Search
        </button>
      )}
    </div>
  );
}

/* ══ Main page ══ */
export default function BrowseByLocationPage() {
  useFonts();
  const navigate = useNavigate();

  const handleLocationSearch = (location) => {
    navigate(`/search?location=${encodeURIComponent(location)}`);
  };

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`* { box-sizing: border-box; }`}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.n100}` }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => (e.currentTarget.style.color = T.n800)}
          onMouseLeave={e => (e.currentTarget.style.color = T.n400)}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
      </div>

      {/* ── Hero ── */}
      <div style={{ padding: "clamp(48px,7vw,88px) 20px clamp(40px,5vw,60px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 20 }}>
            <Icon name="MapPin" size={13} color={T.g500} strokeWidth={2.2} />
            Browse by location
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(38px,7vw,62px)", fontWeight: 400, margin: "0 0 18px", lineHeight: 1.07, color: T.n900, letterSpacing: "-0.03em", maxWidth: 580 }}>
            Pros in your{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>neighbourhood</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 32px", maxWidth: 520 }}>
            JiraniApp is live across Nairobi, Mombasa, Kisumu, and Nakuru. Browse verified
            professionals by city and area, or search your specific estate below.
          </p>
          <LocationSearchBar onSearch={handleLocationSearch} />
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px clamp(80px,10vw,120px)" }}>

        {/* ── Coverage overview stats ── */}
        <div style={{ paddingTop: "clamp(36px,5vw,52px)", display: "flex", flexWrap: "wrap", gap: 12, marginBottom: "clamp(36px,5vw,52px)" }}>
          {[
            { num: "4",    label: "Cities covered" },
            { num: "700+", label: "Verified professionals" },
            { num: "40+",  label: "Neighbourhoods served" },
            { num: "24h",  label: "Typical booking lead time" },
          ].map(s => (
            <div key={s.label} style={{
              flex: "1 1 140px", padding: "18px 20px",
              borderRadius: 14, border: `1px solid ${T.n150}`,
              background: T.n50,
            }}>
              <div style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,30px)", fontWeight: 400, color: T.n900, letterSpacing: "-0.03em", lineHeight: 1, marginBottom: 5 }}>
                {s.num}
              </div>
              <div style={{ fontSize: 13, color: T.n400, lineHeight: 1.4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── City cards ── */}
        <div>
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Cities and areas
            </span>
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Where we <em style={{ fontStyle: "italic", color: T.g600 }}>operate</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>
            Select a city to see available neighbourhoods and the most booked services in each area.
            Click any neighbourhood to go straight to filtered search results.
          </p>

          <div>
            {CITIES.map((city, i) => (
              <CityCard key={city.id} city={city} defaultOpen={i === 0} />
            ))}
          </div>
        </div>

        {/* ── How location search works ── */}
        <div style={{ marginTop: "clamp(48px,7vw,72px)" }}>
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              How it works
            </span>
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Why location <em style={{ fontStyle: "italic", color: T.g600 }}>matters</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            JiraniApp is built around proximity. Here is why we prioritise it.
          </p>
          <div>
            <InfoRow
              icon="Navigation"
              title="Hyperlocal matching first"
              desc="When you search, JiraniApp surfaces professionals closest to your specified location first. A pro in your estate is faster to arrive, more likely to know your building or area, and easier to follow up with if needed."
            />
            <InfoRow
              icon="Clock"
              title="Faster response and arrival times"
              desc="A professional 10 minutes away responds to a booking differently than one 45 minutes away. Local pros can often accommodate same-day or next-day bookings more flexibly. Distance is always shown on every profile so you can factor it in."
            />
            <InfoRow
              icon="Star"
              title="Reviews from people in your area"
              desc="When you view a JiraniPro, you can see reviews from clients in nearby areas. This gives you a more relevant picture of how they perform in your specific neighbourhood, not just their overall average."
            />
            <InfoRow
              icon="Shield"
              title="Accountability through proximity"
              desc="Local professionals have a reputation to maintain in their community. JiraniPros who work regularly in your area tend to be more reliable and more careful about the quality of their work, because they are building a local name."
            />
            <InfoRow
              icon="Zap"
              title="Live availability by area"
              desc="JiraniPros set their availability zone as part of their profile. When you filter by your location, you only see professionals who have indicated they serve that area, so every result is a genuine option, not just a name on a list."
            />
          </div>
        </div>

        {/* ── Coming soon banner ── */}
        <div style={{ marginTop: "clamp(40px,6vw,60px)", padding: "clamp(22px,4vw,32px)", borderRadius: 16, background: T.n50, border: `1px solid ${T.n150}`, display: "flex", alignItems: "flex-start", gap: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: T.white, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
            <Icon name="Globe" size={18} color={T.n400} strokeWidth={1.7} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.n800, marginBottom: 5 }}>Expanding soon</div>
            <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.7, margin: "0 0 14px" }}>
              We are actively growing our network across Kenya. Cities including Eldoret, Thika,
              Malindi, and Naivasha are on our expansion roadmap. If you would like JiraniApp in
              your area sooner, let us know through the{" "}
              <button onClick={() => navigate("/help")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: T.g600, fontSize: 14, fontFamily: T.sans, fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 2 }}>
                Help and Support
              </button>{" "}
              page.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {["Eldoret", "Thika", "Malindi", "Naivasha", "Nyeri", "Meru"].map(city => (
                <span key={city} style={{
                  fontSize: 12, color: T.n400, background: T.white,
                  border: `1px solid ${T.n150}`, borderRadius: 99,
                  padding: "4px 12px", fontWeight: 500,
                }}>
                  {city}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Bottom CTA ── */}
        <div style={{ marginTop: "clamp(48px,7vw,72px)", padding: "clamp(32px,5vw,48px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50 }}>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Find a pro near{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>you</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            Enter your estate or neighbourhood and see who is available right now.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => navigate("/search")}
              style={{
                padding: "13px 28px", background: T.g500, color: T.white,
                border: "none", borderRadius: 11, fontSize: 14, fontWeight: 600,
                fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
                display: "inline-flex", alignItems: "center", gap: 8,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = T.g600)}
              onMouseLeave={e => (e.currentTarget.style.background = T.g500)}>
              Search near me
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button
              onClick={() => navigate("/find-a-jiranipro")}
              style={{
                padding: "13px 28px", background: "transparent", color: T.n700,
                border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 14, fontWeight: 600,
                fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = T.n400)}
              onMouseLeave={e => (e.currentTarget.style.borderColor = T.n200)}>
              How to find a JiraniPro
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}